"use client";

import React, { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { toast } from "sonner";

// Cache for user names to avoid repeated API calls
const userNameCache = new Map<string, string>();

interface GlobalChatNotificationHandlerProps {
  onNewMessage?: (channelId: string, senderName: string) => void;
}

// Debug component for testing notifications
const NotificationDebugPanel = () => {
  const testNotification = () => {
    const testEvent = new CustomEvent("stream-chat-message-new", {
      detail: {
        channelId: "test-channel",
        senderName: "Test User",
        message: { text: "Test message" },
        senderId: "test-user-id",
      },
    });
    window.dispatchEvent(testEvent);
  };

  const testToast = () => {
    toast.info("💬 Test notification", {
      duration: Infinity,
      dismissible: true,
      closeButton: true, // Add close button
      action: {
        label: "Test Action",
        onClick: () => {},
      },
    });
  };

  // Only show in development
  if (process.env.NODE_ENV !== "development") {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 z-[9999] bg-white border border-gray-300 rounded-lg p-3 shadow-lg">
      <h3 className="text-sm font-bold mb-2">Chat Notification Debug</h3>
      <div className="space-y-2">
        <button
          onClick={testNotification}
          className="block w-full px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Test Notification
        </button>
        <button
          onClick={testToast}
          className="block w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
        >
          Test Toast
        </button>
      </div>
    </div>
  );
};

const GlobalChatNotificationHandler: React.FC<GlobalChatNotificationHandlerProps> = ({
  onNewMessage,
}) => {
  const { data: session } = useSession();
  const [lastChannelId, setLastChannelId] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState<boolean>(false);
  const [activeNotifications, setActiveNotifications] = useState<Set<string>>(new Set());
  const [processedMessages, setProcessedMessages] = useState<Set<string>>(new Set()); // Track processed messages

  // Function to extract user ID from sender ID and fetch user name
  const getUserNameFromChannelId = async (channelId: string, senderId: string): Promise<string> => {
    // Check cache first
    if (userNameCache.has(senderId)) {
      return userNameCache.get(senderId)!;
    }

    try {
      // Extract entity ID from sender ID (format: "entityId_timestamp")
      // For senderId like "5731749223_679714", we want the second part "679714"
      const parts = senderId.split("_");
      let entityId;

      if (parts.length >= 2) {
        entityId = parts[1];
      } else {
        entityId = parts[0];
      }

      // Fetch user name from dedicated API endpoint
      const response = await fetch(`/next/api/user/name?entityId=${entityId}`);
      if (response.ok) {
        const userData = await response.json();

        if (userData && userData.name) {
          // Cache the result
          userNameCache.set(senderId, userData.name);
          return userData.name;
        }
      }
    } catch (error) {
      // Error fetching user name
    }

    // Fallback to sender ID if API call fails
    return senderId;
  };

  // Function to dismiss a specific notification
  const dismissNotification = (notificationId: string) => {
    setActiveNotifications((prev) => {
      const newSet = new Set(prev);
      newSet.delete(notificationId);
      return newSet;
    });
  };

  // Test function to manually trigger a notification
  const testNotification = () => {
    const testEvent = new CustomEvent("stream-chat-message-new", {
      detail: {
        channelId: "test-channel",
        senderName: "Test User",
        message: { text: "Test message" },
        senderId: "test-user-id",
      },
    });
    window.dispatchEvent(testEvent);
  };

  // Test function to check if event listeners are working
  const testEventSystem = () => {
    // Test if we can dispatch and receive events
    const testEvent = new CustomEvent("test-event-system", {
      detail: { test: true, timestamp: Date.now() },
    });
    window.dispatchEvent(testEvent);
  };

  useEffect(() => {
    if (!session?.user) {
      return;
    }

    const handleCustomMessageEvent = async (event: CustomEvent) => {
      const { channelId, senderName, message } = event.detail;

      if (!channelId || channelId === "unknown" || !senderName) {
        return;
      }

      // Create a unique message ID to prevent duplicates (but don't persist across refreshes)
      const messageId = `${channelId}_${message?.id || Date.now()}_${senderName}`;

      // Check if we've already processed this message in current session
      if (processedMessages.has(messageId)) {
        return;
      }

      // Add to processed messages for current session only
      setProcessedMessages((prev) => new Set(prev).add(messageId));

      // Clean the channel ID - remove the channel type prefix if present
      let cleanChannelId = channelId;
      if (channelId.includes(":")) {
        cleanChannelId = channelId.split(":")[1];
      }

      // Check if sender name is proper or needs to be fetched
      let finalSenderName = senderName;
      const senderId = message?.user?.id || event.detail.senderId;

      // First, try to get the name from the Stream Chat message object
      if (
        message?.user?.name &&
        message.user.name !== "Admin" &&
        !message.user.name.includes("_")
      ) {
        finalSenderName = message.user.name;
      }
      // If the name looks like an ID or is 'Admin', try to fetch the real name
      else if (
        senderId &&
        (senderName === "Admin" ||
          senderName === "admin" ||
          senderName.includes("_") ||
          senderName === senderId)
      ) {
        try {
          finalSenderName = await getUserNameFromChannelId(cleanChannelId, senderId);
        } catch (error) {
          finalSenderName = senderName; // Fallback to original name
        }
      }

      // Create unique notification ID
      const notificationId = `${cleanChannelId}_${Date.now()}`;

      // Always auto-open chat window for new messages (but only once)
      const openChatEvent = new CustomEvent("open-chat-popup", {
        detail: {
          channelId: cleanChannelId,
          senderName: finalSenderName,
        },
      });
      window.dispatchEvent(openChatEvent);

      // Check if this is a new message from a different channel or if chat is not open
      const isNewChannel = lastChannelId !== cleanChannelId;
      const shouldShowNotification = !isChatOpen && (isNewChannel || true); // Only show if chat is NOT open

      if (shouldShowNotification) {
        // Add a small delay before showing notification to let chat window open first
        setTimeout(() => {
          // Add notification to active set
          setActiveNotifications((prev) => new Set(prev).add(notificationId));

          try {
            // Show persistent toast notification with sender's name (no auto-hide)
            const toastId = toast.info(`💬 New message from ${finalSenderName}`, {
              id: notificationId, // Use unique ID to prevent duplicates
              action: {
                label: "Open Chat",
                onClick: () => {
                  // Dispatch a custom event to open chat popup
                  const openChatEvent = new CustomEvent("open-chat-popup", {
                    detail: {
                      channelId: cleanChannelId,
                      senderName: finalSenderName,
                    },
                  });
                  window.dispatchEvent(openChatEvent);
                  // Dismiss this notification when chat is opened
                  dismissNotification(notificationId);
                  toast.dismiss(notificationId);
                },
              },
              duration: Infinity, // Never auto-hide chat notifications
              dismissible: true,
              closeButton: true, // Add close button
              onDismiss: () => {
                // Remove from active notifications when dismissed
                dismissNotification(notificationId);
              },
            });
          } catch (error) {
            // Error creating toast
          }
        }, 1000); // 1 second delay
      }

      // Update last channel ID
      setLastChannelId(cleanChannelId);
    };

    // Listen for chat open/close events to track state
    const handleChatOpenEvent = () => {
      setIsChatOpen(true);

      // Dismiss all active notifications when chat is opened
      activeNotifications.forEach((notificationId) => {
        toast.dismiss(notificationId);
      });
      setActiveNotifications(new Set());
    };

    const handleChatCloseEvent = () => {
      setIsChatOpen(false);
    };

    window.addEventListener(
      "stream-chat-message-new",
      handleCustomMessageEvent as unknown as EventListener
    );
    window.addEventListener("chat-popup-opened", handleChatOpenEvent);
    window.addEventListener("chat-popup-closed", handleChatCloseEvent);

    // Expose test function globally for debugging
    if (typeof window !== "undefined") {
      (window as any).testChatNotification = testNotification;
      (window as any).testEventSystem = testEventSystem;
      (window as any).getNotificationState = () => ({
        hasSession: !!session?.user,
        sessionUser: session?.user?.entity_id,
        isChatOpen,
        processedMessagesCount: processedMessages.size,
        activeNotificationsCount: activeNotifications.size,
      });
    }

    return () => {
      window.removeEventListener(
        "stream-chat-message-new",
        handleCustomMessageEvent as unknown as EventListener
      );
      window.removeEventListener("chat-popup-opened", handleChatOpenEvent);
      window.removeEventListener("chat-popup-closed", handleChatCloseEvent);
    };
  }, [session?.user, isChatOpen, lastChannelId, activeNotifications, processedMessages]);

  return null;
};

export default GlobalChatNotificationHandler;
